import React from 'react';
import MediaPlayer from './MediaPlayer.jsx';
import {
    formatAutoFlashInterval,
    sliderToInterval,
    intervalToSlider
} from '../utils/time.js';

const UploadControls = ({
    isDevMode,
    entitlements,
    handleDrop,
    handleDragOver,
    fileName,
    triggerFileUpload,
    fileInputRef,
    handleFileChange,
    mediaSrc,
    handleCancelUpload,
    fileType,
    videoPlayerRef,
    audioPlayerRef,
    togglePlayPause,
    isPlaying,
    handleSeek,
    currentTime,
    mediaDuration,
    syncMediaState,
    isProcessing,
    handleProcessAudio,
    audioCacheId,
    isUploadAutoFlashOn,
    setIsUploadAutoFlashOn,
    uploadAutoFlashInterval,
    setUploadAutoFlashInterval,
    duration,
    setDuration,
    isGenerating,
    handleUploadFlash,
    rebuildPreview
}) => {
    const outOfAudio = !isDevMode && typeof entitlements?.audioMinutesRemaining === 'number' && entitlements.audioMinutesRemaining <= 0;
    const outOfCredits = !isDevMode && Number(entitlements?.creditsBalance ?? 0) <= 0;
    try { console.log('[upload] UploadControls props', { hasMediaSrc: !!mediaSrc, fileName, fileType, isPlaying, mediaDuration }); } catch {}
    return (
        <div>
            <div className="text-slate-300 mb-4">
                <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Drag & drop or click "Select File" to upload an audio or video.</li>
                    <li>Play the media, then hit "Flash It!" to create a card.</li>
                </ol>
            </div>
            {mediaSrc && (
                <div className="flex items-center gap-3">
                    <button type="button" onClick={triggerFileUpload} className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500">
                        Change File
                    </button>
                    {fileName && <span className="text-sm text-slate-300">Selected: {fileName}</span>}
                </div>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch(_){} handleFileChange(e); }}
                accept="audio/*,video/*"
                tabIndex={-1}
                aria-hidden="true"
                // inert is non-standard but supported by many browsers; prevents focus/interaction
                inert=""
                style={{ position: 'fixed', top: '-10000px', left: '-10000px', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
            />
            {!mediaSrc && (
                <div className="mt-4">
                    <label className="block text-sm text-slate-300 mb-2">Select an audio/video file</label>
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={triggerFileUpload} className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600">Browse…</button>
                        <span className="text-xs text-slate-400">or drag & drop below</span>
                    </div>
                    <div
                        className="mt-3 rounded-2xl border border-dashed border-white/15 bg-slate-900/60 p-8 text-center text-slate-400"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        Drop file here
                    </div>
                </div>
            )}
            {mediaSrc && (
                <>
                    {isProcessing && (
                        <div className="mt-3">
                            <button type="button" onClick={handleCancelUpload} className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10">Cancel Upload</button>
                        </div>
                    )}
                    <div className="mt-3">
                        <MediaPlayer
                            fileType={fileType}
                            mediaSrc={mediaSrc}
                            videoPlayerRef={videoPlayerRef}
                            audioPlayerRef={audioPlayerRef}
                            isPlaying={isPlaying}
                            togglePlayPause={togglePlayPause}
                            handleSeek={handleSeek}
                            currentTime={currentTime}
                            mediaDuration={mediaDuration}
                            syncMediaState={syncMediaState}
                        />
                    </div>
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        {fileType === 'video' && !audioCacheId && (
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleProcessAudio(); }}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 disabled:opacity-60"
                            >
                                {isProcessing ? 'Processing...' : '🎧 Process Audio from Video'}
                            </button>
                        )}
                        <button type="button" onClick={() => setIsUploadAutoFlashOn(!isUploadAutoFlashOn)} disabled={fileType === 'video' && !audioCacheId} className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${isUploadAutoFlashOn ? 'text-white bg-teal-600' : 'text-slate-200 bg-slate-800/70'} ring-1 ring-white/10 disabled:opacity-60`}>
                            Auto-Flash
                        </button>
                    </div>
                    {isUploadAutoFlashOn && (fileType === 'audio' || audioCacheId) && (
                        <div className="mt-3">
                            <label htmlFor="upload-autoflash-slider" className="block text-sm text-slate-300">Auto-Flash Interval: <span className="text-teal-300">{formatAutoFlashInterval(uploadAutoFlashInterval)}</span></label>
                            <div className="relative mt-2">
                                <div className="h-2 w-full rounded-full bg-slate-800/80"></div>
                                <div className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600" style={{ width: `${(intervalToSlider(uploadAutoFlashInterval) / 8) * 100}%` }}></div>
                                <input id="upload-autoflash-slider" type="range" min="0" max="8" step="1" value={intervalToSlider(uploadAutoFlashInterval)} onChange={(e) => setUploadAutoFlashInterval(sliderToInterval(Number(e.target.value)))} disabled={isPlaying && isUploadAutoFlashOn} className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer" />
                            </div>
                            <p className="text-xs text-slate-400 mt-1">⚡ Automatically creating a card every {formatAutoFlashInterval(uploadAutoFlashInterval)}.</p>
                        </div>
                    )}
                </>
            )}
            <div className="mt-4">
                <label htmlFor="duration-slider-upload" className="block text-sm text-slate-300">Capture Last: <span className="text-teal-300">{duration}</span> seconds of audio</label>
                <div className="relative mt-2">
                    <div className="h-2 w-full rounded-full bg-slate-800/80"></div>
                    <div className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600" style={{ width: `${((duration - 5) / (30 - 5)) * 100}%` }}></div>
                    <input id="duration-slider-upload" type="range" min="5" max="30" step="1" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer" />
                    <div className="absolute -top-[6px] h-5 w-5 rounded-full bg-white shadow-lg shadow-teal-500/20 ring-4 ring-teal-500/30" style={{ left: `calc(${((duration - 5) / (30 - 5)) * 100}% - 10px)`, pointerEvents: 'none' }}></div>
                </div>
            </div>
            <div className="mt-5">
                <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUploadFlash(); }}
                    className="flash-it-button w-full inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-5 text-lg font-semibold ring-1 ring-white/10 disabled:opacity-60"
                    disabled={!mediaSrc || isGenerating || (fileType === 'video' && !audioCacheId) || (isUploadAutoFlashOn && isPlaying) || outOfCredits || outOfAudio}
                >
                    {isGenerating ? 'Generating...' : '⚡ Flash It!'}
                </button>
                {outOfAudio && (
                    <p className="text-xs text-rose-300 mt-2">You are out of audio minutes.</p>
                )}
            </div>
            {mediaSrc && fileType && (
                <div className="mt-3 text-xs text-slate-400">
                    <span>Loaded: {fileName || (fileType.toUpperCase())}</span>
                    <button type="button" onClick={rebuildPreview} className="ml-2 underline underline-offset-2 text-teal-300">Rebuild preview</button>
                </div>
            )}
        </div>
    );
};

export default UploadControls;


